// src/QuoteUploader.js
// Component for uploading and parsing vendor quotes

import React, { useState } from 'react';
import {
  collection,
  addDoc,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import './styles.css';

export default function QuoteUploader({ tenderId, subsystemId, vendorId, onSuccess, onCancel }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [manualEdit, setManualEdit] = useState(false);
  const [formData, setFormData] = useState({
    currency: '',
    delivery: '',
    warranty: '',
    paymentTerms: '',
    total: '',
    exclusions: ''
  });

  function handleFileChange(e) {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file type
      const allowedTypes = [
        'application/pdf',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      
      if (!allowedTypes.includes(selectedFile.type)) {
        alert('Please upload a PDF or Excel file');
        return;
      }

      setFile(selectedFile);
      setExtractedData(null);
      setManualEdit(false);
    }
  }

  async function handleUploadAndExtract() {
    if (!file) {
      alert('Please select a file first');
      return;
    }

    try {
      setUploading(true);

      // Upload file to Firebase Storage
      const timestamp = Date.now();
      const filePath = `quotes/${tenderId}/${subsystemId}/${vendorId}_${timestamp}_${file.name}`;
      const fileRef = ref(storage, filePath);
      
      await uploadBytes(fileRef, file);
      const fileUrl = await getDownloadURL(fileRef);

      setUploading(false);
      setExtracting(true);

      // Call AI extraction API
      const response = await fetch('/api/extract-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'quote',
          fileUrl,
          fileType: file.type.includes('pdf') ? 'pdf' : 'excel',
          context: `Vendor quote for subsystem ${subsystemId}`
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to extract quote data');
      }

      // Set extracted data
      setExtractedData(result.data);
      setFormData({
        currency: result.data.currency || '',
        delivery: result.data.delivery || '',
        warranty: result.data.warranty || '',
        paymentTerms: result.data.paymentTerms || '',
        total: result.data.total?.toString() || '',
        exclusions: result.data.exclusions || ''
      });

      // Store file URL for later
      setExtractedData({ ...result.data, attachmentUrl: fileUrl });

    } catch (error) {
      console.error('Error uploading/extracting quote:', error);
      alert('Failed to process quote: ' + error.message);
    } finally {
      setUploading(false);
      setExtracting(false);
    }
  }

  async function handleSaveQuote() {
    if (!extractedData) {
      alert('Please extract quote data first');
      return;
    }

    // Validate
    if (!formData.total || parseFloat(formData.total) <= 0) {
      alert('Total amount is required and must be greater than 0');
      return;
    }

    try {
      const quoteData = {
        vendorId,
        subsystemId,
        tenderId,
        currency: formData.currency,
        delivery: formData.delivery,
        warranty: formData.warranty,
        paymentTerms: formData.paymentTerms,
        items: extractedData.items || [],
        total: parseFloat(formData.total),
        exclusions: formData.exclusions,
        attachmentUrl: extractedData.attachmentUrl,
        extractedData: extractedData,
        selected: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      await addDoc(
        collection(db, `tenders/${tenderId}/subsystems/${subsystemId}/quotes`),
        quoteData
      );

      alert('Quote saved successfully!');
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error saving quote:', error);
      alert('Failed to save quote: ' + error.message);
    }
  }

  function handleInputChange(field, value) {
    setFormData({ ...formData, [field]: value });
  }

  return (
    <div className="quote-uploader-container">
      <h3>Upload Vendor Quote</h3>

      {/* File Upload */}
      <div className="upload-section">
        <div className="file-input-wrapper">
          <input
            type="file"
            accept=".pdf,.xlsx,.xls,.csv"
            onChange={handleFileChange}
            id="quote-file-input"
            style={{ display: 'none' }}
          />
          <label htmlFor="quote-file-input" className="file-input-label">
            {file ? file.name : 'Choose File (PDF or Excel)'}
          </label>
        </div>

        <button
          onClick={handleUploadAndExtract}
          disabled={!file || uploading || extracting}
          className="extract-button"
        >
          {uploading ? 'Uploading...' : extracting ? 'Extracting...' : 'Upload & Extract'}
        </button>
      </div>

      {/* Extracted Data */}
      {extractedData && (
        <div className="extracted-data-section">
          <div className="section-header">
            <h4>Extracted Quote Data</h4>
            <button
              onClick={() => setManualEdit(!manualEdit)}
              className="edit-toggle-button"
            >
              {manualEdit ? 'Cancel Edit' : 'Edit Manually'}
            </button>
          </div>

          <div className="extracted-data-form">
            <div className="form-row">
              <div className="form-group">
                <label>Currency</label>
                {manualEdit ? (
                  <input
                    type="text"
                    value={formData.currency}
                    onChange={e => handleInputChange('currency', e.target.value)}
                  />
                ) : (
                  <div className="read-only-field">{formData.currency || '-'}</div>
                )}
              </div>

              <div className="form-group">
                <label>Total Amount *</label>
                {manualEdit ? (
                  <input
                    type="number"
                    step="0.01"
                    value={formData.total}
                    onChange={e => handleInputChange('total', e.target.value)}
                    required
                  />
                ) : (
                  <div className="read-only-field">{formData.total || '-'}</div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Delivery Time</label>
                {manualEdit ? (
                  <input
                    type="text"
                    value={formData.delivery}
                    onChange={e => handleInputChange('delivery', e.target.value)}
                  />
                ) : (
                  <div className="read-only-field">{formData.delivery || '-'}</div>
                )}
              </div>

              <div className="form-group">
                <label>Warranty</label>
                {manualEdit ? (
                  <input
                    type="text"
                    value={formData.warranty}
                    onChange={e => handleInputChange('warranty', e.target.value)}
                  />
                ) : (
                  <div className="read-only-field">{formData.warranty || '-'}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Payment Terms</label>
              {manualEdit ? (
                <textarea
                  value={formData.paymentTerms}
                  onChange={e => handleInputChange('paymentTerms', e.target.value)}
                  rows={2}
                />
              ) : (
                <div className="read-only-field">{formData.paymentTerms || '-'}</div>
              )}
            </div>

            <div className="form-group">
              <label>Exclusions</label>
              {manualEdit ? (
                <textarea
                  value={formData.exclusions}
                  onChange={e => handleInputChange('exclusions', e.target.value)}
                  rows={3}
                />
              ) : (
                <div className="read-only-field">{formData.exclusions || '-'}</div>
              )}
            </div>

            {extractedData.items && extractedData.items.length > 0 && (
              <div className="form-group">
                <label>Line Items ({extractedData.items.length})</label>
                <div className="items-preview">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Qty</th>
                        <th>Unit Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extractedData.items.slice(0, 5).map((item, index) => (
                        <tr key={index}>
                          <td>{item.description}</td>
                          <td>{item.qty}</td>
                          <td>{item.unitPrice}</td>
                          <td>{item.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {extractedData.items.length > 5 && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                      ... and {extractedData.items.length - 5} more items
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="uploader-actions">
            <button onClick={onCancel} className="cancel-button">
              Cancel
            </button>
            <button onClick={handleSaveQuote} className="save-button">
              Save Quote
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
